import React, { useState } from "react";
import MarkdownRenderer from "../common/MarkdownRenderer";

interface ResearchReport {
  introduction: string;
  body: string;
  conclusion: string;
  sources: Array<{ title: string; url: string }>;
  costs?: string;
}

interface ResearchReportRendererProps {
  reportData: string; // JSON string of the research report
  className?: string;
  context?: "chat" | "standalone";
}

export const ResearchReportRenderer: React.FC<ResearchReportRendererProps> = ({
  reportData,
  className = "",
  context = "chat"
}) => {
  const [isReportExpanded, setIsReportExpanded] = useState(false);

  // Render a source card for research reports
  const renderSourceCard = (source: { title: string; url: string }) => (
    <div key={source.url} className="research-report__source">
      <a
        className="research-report__source-link"
        href={source.url}
        rel="noopener noreferrer"
        target="_blank"
      >
        {source.title || source.url.split("/")[2] || source.url.slice(0, 30) + "..."}
      </a>
    </div>
  );

  const renderInnerReport = (report: ResearchReport) => {
    return (
      <>
        <div className="research-report__content">
          <div className="research-report__section">
            <div className="chat-message__markdown">
              <MarkdownRenderer markdown={report.introduction} />
            </div>
          </div>

          <div className="research-report__section">
            <div className="chat-message__markdown">
              <MarkdownRenderer markdown={report.body} />
            </div>
          </div>

          <div className="research-report__section">
            <div className="chat-message__markdown">
              <MarkdownRenderer markdown={report.conclusion} />
            </div>
          </div>

          {report.sources && report.sources.length > 0 && (
            <div className="research-report__section">
              <h3 className="research-report__section-title">Sources</h3>
              <div className="research-report__sources">
                {report.sources.map(renderSourceCard)}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  try {
    const report: ResearchReport = JSON.parse(reportData);

    // For standalone context, always show expanded without toggle
    if (context === "standalone") {
      return (
        <div className={`research-report ${className}`}>
          {renderInnerReport(report)}
        </div>
      );
    }

    // Original chat context behavior with expand/collapse
    return (
      <div className={`research-report ${className}`}>
        <div className={`research-report__header ${isReportExpanded ? "expanded" : ""}`}>
          {!isReportExpanded && (
            <div className="research-report__title">
              <MarkdownRenderer markdown={report.introduction.split("\n")[0]} />
            </div>
          )}
          <button
            className={`research-report__toggle ${isReportExpanded ? "right-aligned" : ""}`}
            type="button"
            onClick={() => setIsReportExpanded(!isReportExpanded)}
          >
            {isReportExpanded ? "Collapse" : "See the full report"}
          </button>
        </div>

        {isReportExpanded && (
          renderInnerReport(report)
        )}
      </div>
    );
  } catch (error) {
    console.error('Error rendering research report:', error);
    return (
      <div className={`research-report-error ${className}`}>
        <p>Error displaying research report</p>
      </div>
    );
  }
};
